import { ref, computed, onMounted, nextTick, unref, type Component, type MaybeRef } from 'vue';
import { isAxiosError } from 'axios';
import { useRoute } from 'vue-router';

import {
  type NotificationResponse,
  useCustomToast,
  useTableFilterModal,
  NotificationStatus,
  NotificationPeriodicity,
  formatDateForApi,
  useUpdateUrl,
  useInfiniteScrollObserver,
} from '@/shared/lib';
import { DEFAULT_ORDER_OPTION } from '@/shared/config';
import { useFilterStore } from '@/entities/Filter';

import { notificationApi } from '../api/notification.api';
import { NOTIFICATION_EXPORT_GENERIC_RETRY_MESSAGE } from '../config/messages';

import { useNotification } from './useNotification';
import { NotificationFilterModal } from '../ui';
import type { NotificationItemsPerPage, NotificationSearchType } from './types';

export type NotificationFilters = {
  periodicity?: NotificationPeriodicity | null;
  status?: NotificationStatus | null;
  startDate?: Date | null;
  endDate?: Date | null;
  customPeriodicity?: number | null;
  customPeriodicityUnit?: string | null;
  createdBy?: number | null;
};

export interface UseNotificationListOptions {
  createdBy?: MaybeRef<number | null | undefined>;
  headers: string[];
  canCreateNotification: boolean;
  filterModalComponent?: Component;
}

const NOTIFICATIONS_INFINITE_SCROLL_PAGE_SIZE = 30;

function getExportErrorSubtitle(error: unknown): string {
  if (isAxiosError(error)) {
    return NOTIFICATION_EXPORT_GENERIC_RETRY_MESSAGE;
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return NOTIFICATION_EXPORT_GENERIC_RETRY_MESSAGE;
}

export function useNotificationList(options: UseNotificationListOptions) {
  const route = useRoute();
  const { fetchNotification } = useNotification();
  const toast = useCustomToast();
  const filterStore = useFilterStore();
  const { updateUrl } = useUpdateUrl();

  const notifications = ref<NotificationResponse[]>([]);
  const allNotifications = ref<NotificationResponse[]>([]);
  const hasMoreNotifications = ref(true);
  const loading = ref(false);
  const loadingMore = ref(false);
  const loadMoreFailed = ref(false);
  const itemsPerPage = ref<NotificationItemsPerPage>(10);
  const currentPage = ref(1);
  const totalItems = ref(0);
  const totalPages = ref(1);
  const appliedFilters = ref<Partial<NotificationFilters>>({});
  const searchTerm = ref('');
  const selectedNotificationIds = ref<number[]>([]);
  const isCreateModalOpen = ref(false);
  const isEditModalOpen = ref(false);
  const editNotificationId = ref<number | null>(null);
  const searchType = ref<NotificationSearchType>('notifications');
  const currentSort = ref<string | null>(null);
  const currentOrder = ref<string | null>(null);
  const isExportInProgress = ref(false);

  const readQueryString = (value: unknown): string | undefined => {
    if (Array.isArray(value)) {
      return typeof value[0] === 'string' ? value[0] : undefined;
    }

    return typeof value === 'string' ? value : undefined;
  };

  const readQueryNumber = (value: unknown): number | undefined => {
    const rawValue = readQueryString(value);

    if (!rawValue) {
      return undefined;
    }

    const parsed = Number(rawValue);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const readQueryDate = (value: unknown): Date | undefined => {
    const rawValue = readQueryString(value);

    if (!rawValue) {
      return undefined;
    }

    const parsed = new Date(rawValue);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  };

  const isNotificationSearchType = (value: unknown): value is NotificationSearchType =>
    value === 'notifications' || value === 'recipient';

  const isNotificationStatus = (value: unknown): value is NotificationStatus =>
    typeof value === 'number' && value in NotificationStatus;

  const isNotificationPeriodicity = (value: unknown): value is NotificationPeriodicity =>
    typeof value === 'number' && value in NotificationPeriodicity;

  const hydrateFromQuery = () => {
    const page = readQueryNumber(route.query.page);
    const status = readQueryNumber(route.query.status);
    const periodicity = readQueryNumber(route.query.periodicity);
    const createdBy = readQueryNumber(route.query.createdBy);
    const customPeriodicity = readQueryNumber(route.query.customPeriodicity);
    const searchTypeQuery = readQueryString(route.query.searchType);
    const sortQuery = readQueryString(route.query.sort);
    const orderQuery = readQueryString(route.query.order);

    currentPage.value = page && page > 0 ? page : 1;
    searchTerm.value = readQueryString(route.query.term) ?? '';
    searchType.value = isNotificationSearchType(searchTypeQuery)
      ? searchTypeQuery
      : 'notifications';
    currentSort.value = sortQuery ?? null;
    currentOrder.value = orderQuery ?? null;
    appliedFilters.value = {
      status: isNotificationStatus(status) ? status : null,
      periodicity: isNotificationPeriodicity(periodicity) ? periodicity : null,
      startDate: readQueryDate(route.query.startDate) ?? null,
      endDate: readQueryDate(route.query.endDate) ?? null,
      customPeriodicity: customPeriodicity ?? null,
      customPeriodicityUnit: readQueryString(route.query.customPeriodicityUnit) ?? null,
      createdBy: createdBy ?? null,
    };
  };

  const syncUrl = async (page = currentPage.value) => {
    await updateUrl({
      search: searchTerm.value.trim(),
      page,
      sort:
        currentSort.value && currentOrder.value
          ? { sort: currentSort.value, order: currentOrder.value as 'asc' | 'desc' }
          : undefined,
      filter: {
        status: appliedFilters.value.status ?? undefined,
        periodicity: appliedFilters.value.periodicity ?? undefined,
        startDate: appliedFilters.value.startDate ?? undefined,
        endDate: appliedFilters.value.endDate ?? undefined,
        customPeriodicity: appliedFilters.value.customPeriodicity ?? undefined,
        customPeriodicityUnit: appliedFilters.value.customPeriodicityUnit ?? undefined,
        createdBy: unref(options.createdBy) ?? appliedFilters.value.createdBy ?? undefined,
      },
      query: {
        searchType: searchType.value,
      },
    });
  };

  const isInfiniteScrollMode = computed(() => itemsPerPage.value === 'all');

  const resolveRequestItemsPerPage = (): number => {
    const value = itemsPerPage.value;
    return typeof value === 'number' ? value : NOTIFICATIONS_INFINITE_SCROLL_PAGE_SIZE;
  };

  const buildNotificationListParams = (args: {
    page: number;
    itemsPerPage: number;
    term?: string;
    sort?: string;
    order?: string;
    useFilters: boolean;
  }) => {
    const filterParams = args.useFilters
      ? {
          status: appliedFilters.value.status?.toString() ?? undefined,
          periodicity: appliedFilters.value.periodicity?.toString() ?? undefined,
          startDate: appliedFilters.value.startDate
            ? (formatDateForApi(appliedFilters.value.startDate) ?? undefined)
            : undefined,
          endDate: appliedFilters.value.endDate
            ? (formatDateForApi(appliedFilters.value.endDate) ?? undefined)
            : undefined,
          customPeriodicity: appliedFilters.value.customPeriodicity ?? undefined,
          customPeriodicityUnit: appliedFilters.value.customPeriodicityUnit ?? undefined,
        }
      : {};

    const term = args.term ?? searchTerm.value;

    return {
      page: args.page,
      itemsPerPage: args.itemsPerPage,
      term: term ?? undefined,
      sort: args.sort ?? currentSort.value ?? undefined,
      order: args.order ?? currentOrder.value ?? undefined,
      searchType: searchType.value,
      ...filterParams,
      createdBy:
        unref(options.createdBy) ??
        (args.useFilters ? (appliedFilters.value.createdBy ?? undefined) : undefined),
    };
  };

  const orderOptions = [
    { ...DEFAULT_ORDER_OPTION, label: 'Por notificação mais recente' },
    { label: 'Por notificação mais antiga', sort: 'createdAt', order: 'asc' },
    { label: 'Por ordem alfabética crescente', sort: 'name', order: 'asc' },
    { label: 'Por ordem alfabética decrescente', sort: 'name', order: 'desc' },
  ];

  let requestSeq = 0;

  const loadNotifications = async (
    page: number = 1,
    searchTermArg?: string,
    sort?: string,
    order?: string,
    useFilters: boolean = true,
    append: boolean = false
  ) => {
    if (
      append &&
      (!isInfiniteScrollMode.value ||
        loading.value ||
        loadingMore.value ||
        !hasMoreNotifications.value ||
        loadMoreFailed.value)
    ) {
      return;
    }

    const seq = ++requestSeq;
    loadingMore.value = append;
    loading.value = !append;
    try {
      const response = await fetchNotification(
        buildNotificationListParams({
          page,
          itemsPerPage: resolveRequestItemsPerPage(),
          term: searchTermArg,
          sort,
          order,
          useFilters,
        })
      );
      if (seq !== requestSeq) return;

      totalItems.value = response.metadata.total;
      totalPages.value = response.metadata.totalPages;
      currentPage.value = response.metadata.page;

      if (isInfiniteScrollMode.value) {
        allNotifications.value = append
          ? [...allNotifications.value, ...response.data]
          : [...response.data];
        notifications.value = allNotifications.value;
        hasMoreNotifications.value = allNotifications.value.length < totalItems.value;
      } else {
        notifications.value = response.data;
        hasMoreNotifications.value = false;
      }
    } catch {
      if (seq !== requestSeq) return;

      if (append) {
        loadMoreFailed.value = true;
      } else {
        notifications.value = [];
        allNotifications.value = [];
        totalItems.value = 0;
        totalPages.value = 1;
        hasMoreNotifications.value = false;
      }
      toast.showErrorToast({
        title: 'Erro ao carregar notificações',
        message: 'Tente novamente em instantes.',
        type: 'error',
      });
    } finally {
      if (seq === requestSeq) {
        loading.value = false;
        loadingMore.value = false;
      }
    }
  };

  const loadMoreNotifications = async () => {
    if (loading.value || loadingMore.value || !hasMoreNotifications.value || loadMoreFailed.value) {
      return;
    }
    await loadNotifications(
      currentPage.value + 1,
      searchTerm.value,
      undefined,
      undefined,
      true,
      true
    );
  };

  useInfiniteScrollObserver({
    enabled: isInfiniteScrollMode,
    loadingMore,
    hasMore: hasMoreNotifications,
    onLoadMore: loadMoreNotifications,
  });

  const resetPaginationState = (page = 1) => {
    currentPage.value = page;
    hasMoreNotifications.value = true;
    loadMoreFailed.value = false;
  };

  const reloadCurrentNotifications = async () => {
    if (isInfiniteScrollMode.value) {
      resetPaginationState();
      await loadNotifications(1, searchTerm.value);
      return;
    }
    await loadNotifications(currentPage.value, searchTerm.value);
  };

  const scrollToPagination = async () => {
    await nextTick();
    const paginationNav = document.querySelector<HTMLElement>(
      'nav[role="navigation"][aria-label*="páginas"]'
    );
    if (paginationNav) {
      paginationNav.scrollIntoView({ behavior: 'smooth', block: 'center' });
      paginationNav.querySelector<HTMLButtonElement>('button[aria-current="page"]')?.focus();
    }
  };

  const onPageChanged = async (page: number) => {
    resetPaginationState(page);
    await syncUrl(page);
    await loadNotifications(page, searchTerm.value);
    await scrollToPagination();
  };

  const handleQuantityChange = async (value: string | number) => {
    itemsPerPage.value = value === 'all' ? 'all' : (Number(value) as NotificationItemsPerPage);

    const nextPageSize = resolveRequestItemsPerPage();
    const visibleNotifications = notifications.value.slice(0, nextPageSize);
    notifications.value = visibleNotifications;
    allNotifications.value = isInfiniteScrollMode.value ? [...visibleNotifications] : [];

    resetPaginationState();
    await syncUrl(1);
    await loadNotifications(1, searchTerm.value);
  };

  const handleSearch = async (queryString: string) => {
    loading.value = true;
    searchTerm.value = queryString;
    resetPaginationState();
    await syncUrl(1);
    await loadNotifications(1, queryString);
  };

  const handleNotificationDeleted = async () => {
    await reloadCurrentNotifications();
  };

  const handleFilterUpdate = async (filters: Partial<NotificationFilters>) => {
    appliedFilters.value = filters;
    resetPaginationState();
    await syncUrl(1);
    await loadNotifications(1, searchTerm.value, undefined, undefined, true);
  };

  const handleCreateNotification = () => {
    isCreateModalOpen.value = true;
  };

  const handleNotificationCreated = () => {
    reloadCurrentNotifications();
  };

  const handleEditNotification = (id: number) => {
    editNotificationId.value = id;
    isEditModalOpen.value = true;
  };

  const handleNotificationUpdated = () => {
    reloadCurrentNotifications();
  };

  const { isFilterActive, openFilterModal } = useTableFilterModal(
    options.filterModalComponent || NotificationFilterModal,
    filterStore,
    handleFilterUpdate,
    () => appliedFilters.value
  );

  const handleFilterClicked = () => {
    openFilterModal();
  };

  const handleSelectedNotificationsUpdate = (selectedIds: number[]) => {
    selectedNotificationIds.value = selectedIds;
  };

  const hasNotifications = computed(() => notifications.value.length > 0);
  const isExportButtonDisabled = computed(
    () => !hasNotifications.value || isExportInProgress.value
  );

  const handleExportToExcel = async () => {
    if (isExportInProgress.value) return;
    isExportInProgress.value = true;
    try {
      toast.showWarningToast({
        title: 'Exportação em andamento',
        message: 'Por favor, aguarde.',
        type: 'warning',
      });

      const {
        term,
        sort,
        order,
        searchType,
        createdBy,
        status,
        periodicity,
        startDate,
        endDate,
        customPeriodicity,
        customPeriodicityUnit,
      } = buildNotificationListParams({
        page: 1,
        itemsPerPage: resolveRequestItemsPerPage(),
        useFilters: true,
      });

      const { blob, exportedCount, filename } = await notificationApi.exportNotificationsExcel({
        term,
        sort,
        order,
        searchType,
        createdBy,
        status,
        periodicity,
        startDate,
        endDate,
        customPeriodicity,
        customPeriodicityUnit,
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const successMessage =
        exportedCount === 1
          ? '1 notificação exportada'
          : `${exportedCount} notificações exportadas`;

      toast.showSuccessToast({
        title: 'Exportação concluída com sucesso',
        message: successMessage,
        type: 'success',
      });
    } catch (error) {
      toast.showErrorToast({
        title: 'Erro ao exportar',
        message: getExportErrorSubtitle(error),
        type: 'error',
      });
    } finally {
      isExportInProgress.value = false;
    }
  };

  onMounted(() => {
    hydrateFromQuery();
    loadNotifications(currentPage.value, searchTerm.value);
  });

  const handleSearchTypeChange = async (type: string | number) => {
    searchType.value = type as NotificationSearchType;
    await syncUrl(1);
    if (searchTerm.value) {
      resetPaginationState();
      await loadNotifications(1, searchTerm.value);
    }
  };

  const handleSortOptions = async (sort: string, order: string) => {
    currentSort.value = sort;
    currentOrder.value = order;
    resetPaginationState();
    await syncUrl(1);
    await loadNotifications(1, searchTerm.value, sort, order);
  };

  return {
    notifications,
    loading,
    loadingMore,
    hasMoreNotifications,
    itemsPerPage,
    currentPage,
    totalItems,
    totalPages,
    searchTerm,
    searchType,
    selectedNotificationIds,
    isCreateModalOpen,
    isEditModalOpen,
    editNotificationId,
    orderOptions,
    headers: options.headers,
    canCreateNotification: options.canCreateNotification,
    isFilterActive,
    hasNotifications,
    isExportButtonDisabled,
    loadNotifications,
    onPageChanged,
    handleQuantityChange,
    handleSearch,
    handleNotificationDeleted,
    handleFilterUpdate,
    handleCreateNotification,
    handleNotificationCreated,
    handleEditNotification,
    handleNotificationUpdated,
    handleFilterClicked,
    handleSelectedNotificationsUpdate,
    handleExportToExcel,
    handleSearchTypeChange,
    handleSortOptions,
  };
}
